import { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import Location from '../../hooks/Location'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import useThemes from '../../hooks/useThemes'
import useCellDetail from '../../hooks/useCellDetail'
import useThemeInsights from '../../hooks/useThemeInsights'
import useEffectBenchmark from '../../hooks/useEffectBenchmark'
import Modal from '../../components/common/Modal'
import ClassificationExplanation from '../../components/benchmarkDetails/ClassificationExplanation'
import InsightCellsTable from '../../components/benchmarkDetails/InsightCellsTable'
import CellOrgsTable from '../../components/intelligence/CellOrgsTable'
import CellMechanismsSection from '../../components/intelligence/CellMechanismsSection'
import CellInterviewsSection from '../../components/intelligence/CellInterviewsSection'
import InsightEditor from '../../components/intelligence/InsightEditor'
import insightForCell from '../../helpers/intelligence/insightForCell'
import { BENCHMARK_STATE, effectBenchmarkState, publicationBlockedReason } from '../../helpers/intelligence/effectBenchmarkGate'
import { saveInsightDraft } from '../../helpers/intelligence/intelligenceWrites'
import { themeTitle } from '../../helpers/intelligence/themeTitle'
import { segmentToThemeId, themeDetailPath } from '../../helpers/intelligence/themeRoute'
import { segmentToCellId } from '../../helpers/intelligence/cellRoute'
import telwoord from '../../helpers/intelligence/telwoord'
import { ISS_CLASSIFICATION_LABELS } from '../../helpers/sectorInsights/issClassifications'
import cellPublishBlocker from '../../helpers/intelligence/cellPublishBlocker'

/**
 * Het inzicht van deze cel, hier op de pagina (besluit user 2026-09-15: niet
 * op een aparte pagina) en óver deze cel: één mechanisme of activiteit bij
 * één doelgroep. Bestaat het nog niet, dan maakt één knop het concept; de
 * live lijst van het thema laat het daarna verschijnen.
 *
 * Pas een knop als de inzichten van het thema geladen zijn: anders zou een
 * klik een tweede concept naast het bestaande zetten.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * @param {object} props
 * @param {object} props.cell
 * @param {{insights: Array<object>, loaded: boolean, error: (Error|null)}} props.themeInsights
 * @param {string} props.themeTitle
 * @param {string} props.benchmarkState - De stand van de benchmark van het effect (BENCHMARK_STATE)
 */
const CellInsightBlock = ({ cell, themeInsights, themeTitle, benchmarkState }) => {
    const [aanmaken, setAanmaken] = useState({ bezig: false, fout: '' })
    // Zelfde regel als de agenda en de server bij publiceren (ADR-057).
    const blokkade = cellPublishBlocker(cell)
    if (blokkade === 'p2') {
        return (
            <p className="intelligence-state">
                Deze cel haalt de poort (P2) nog niet; wat er nog nodig is staat in de onderbouwing.
            </p>
        )
    }
    if (blokkade === 'classificatie') {
        return (
            <p className="intelligence-state">
                Deze cel is niet toonbaar ({ISS_CLASSIFICATION_LABELS[cell.classification] || 'nog geen classificatie'}); wat er nog nodig is staat in de onderbouwing.
            </p>
        )
    }
    if (themeInsights.error) {
        return (
            <p className="intelligence-state intelligence-state--error">
                De inzichten van dit thema konden niet worden geladen: {themeInsights.error.message}
            </p>
        )
    }
    if (!themeInsights.loaded) return <p className="intelligence-state">Inzicht laden…</p>

    // Eén cel, één inzicht: het celtype patternForTheme beschrijft precies deze
    // cel bij deze doelgroep — niet de ranglijst van de hele dimensie.
    const voorstel = {
        cellType: 'patternForTheme',
        params: { msiId: cell.msiId, dimension: cell.dimension, value: cell.dimensionValue, segment: cell.segment },
    }
    const bestaand = insightForCell(themeInsights.insights, voorstel)
    if (bestaand) {
        return (
            <>
                <p className="intelligence-agenda__hint">
                    Het inzicht gaat over deze cel: dit patroon bij deze doelgroep op dit effect. Schrijf hier de tekst en publiceer.
                </p>
                {/* Na verwijderen verdwijnt de kaart vanzelf uit de live lijst. */}
                {/* Geen tabel boven het tekstvak: de cijfers van deze cel staan al
                    in de kop en de onderbouwing (besluit user 2026-09-15). */}
                <InsightEditor insight={bestaand} themeTitle={themeTitle} onDeleted={() => {}} showOutput={false} />
            </>
        )
    }

    const maak = async () => {
        if (aanmaken.bezig) return
        setAanmaken({ bezig: true, fout: '' })
        try {
            await saveInsightDraft({ cellType: voorstel.cellType, params: voorstel.params })
            // De live lijst van het thema brengt het concept; dan komt de kaart.
            setAanmaken({ bezig: false, fout: '' })
        } catch (err) {
            setAanmaken({ bezig: false, fout: `Het inzicht kon niet worden aangemaakt: ${err.message}` })
        }
    }
    return (
        <>
            {/* Twee poorten: P2 op de cel (hierboven) en de benchmark van het
                effect (ADR-040). 'Klaar om te publiceren' mag alleen staan als
                ze allebei open staan — de server weigert de publicatie anders.
                Een concept schrijven mag wél: dat is eraan werken, niet naar
                buiten brengen.
                AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js */}
            <p className="intelligence-agenda__hint">
                {benchmarkState === BENCHMARK_STATE.ok
                    ? 'Deze cel is klaar om te publiceren.'
                    : publicationBlockedReason(benchmarkState)}
                {' '}Er is nog geen inzicht over dit patroon bij deze doelgroep.
            </p>
            <button type="button" className="intelligence-row-action" disabled={aanmaken.bezig} onClick={maak}>
                Inzicht aanmaken
            </button>
            {aanmaken.bezig && <p className="intelligence-state">Inzicht aanmaken…</p>}
            {aanmaken.fout && <p className="intelligence-state intelligence-state--error">{aanmaken.fout}</p>}
        </>
    )
}

/**
 * Eén cel (waarde × dimensie × effect, gesegmenteerd op doelgroep) met alles
 * wat eronder ligt: de onderbouwing, per organisatie, de concrete mechanismen
 * in de categorie en wat interviews daarover zeggen. Onderaan het inzicht.
 *
 * AI-CONTEXT: See docs/features/intelligence-mechanisme-detail.md
 */
const CellDetailContent = () => {
    const { themeId: themeSegment, cellId: cellSegment } = useParams()
    const history = useHistory()
    const client = Location()[1]

    // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
    const themeId = segmentToThemeId(themeSegment)
    const cellId = segmentToCellId(cellSegment)

    const themes = useThemes()
    const theme = themeId ? themes.themes.find((t) => t.id === themeId) : undefined
    const detail = useCellDetail(cellId || '')
    const themeInsights = useThemeInsights(themeId || '')
    // De publicatiepoort op het EFFECT (ADR-040). Het effect staat in de
    // themasleutel, dus hier is niet op het celdocument te wachten.
    const benchmark = useEffectBenchmark(theme ? { msiId: theme.msiId } : null)
    const [uitlegCel, setUitlegCel] = useState(/** @type {object|null} */(null))
    const [tab, setTab] = useState('onderbouwing')

    const terug = () => history.push(themeId ? themeDetailPath(client, themeId) : `/${client}/intelligence`)
    const kop = theme ? themeTitle(theme) : (themes.loaded ? 'Thema niet gevonden' : 'Thema laden…')

    const inhoud = () => {
        if (!cellId) return <p className="intelligence-state">Dit is geen celsleutel.</p>
        if (detail.error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De cel kon niet worden geladen: {detail.error.message}
                </p>
            )
        }
        if (!detail.loaded || !detail.data) return <p className="intelligence-state">Cel laden…</p>
        if (!detail.data.found) {
            return <p className="intelligence-state">Deze cel bestaat niet (meer) onder de huidige methodiekversie ({detail.data.version}).</p>
        }
        const { cell, orgEffects, mechanisms, interviews, interviewGate, p2Targets } = detail.data
        const tabs = [
            { id: 'onderbouwing', label: 'Onderbouwing' },
            { id: 'orgs', label: 'Per organisatie', aantal: (cell.contributors || []).length },
            ...(mechanisms !== null ? [
                { id: 'mechanisms', label: 'Concrete mechanismen', aantal: mechanisms.length },
                { id: 'interviews', label: 'Uit interviews', aantal: interviews.length },
            ] : []),
            { id: 'insight', label: 'Inzicht' },
        ]
        return (
            <>
                <div className="intelligence-theme-header">
                    <h2 className="intelligence-theme-header__title">{cell.dimensionLabel || cell.dimensionValue}</h2>
                    <ul className="intelligence-theme-header__stats">
                        <li>{cell.classificationLabel || cell.classification}</li>
                        <li>{typeof cell.d === 'number' ? `${cell.d.toFixed(2)} d` : 'geen effectgrootte'}</li>
                        <li>{telwoord(cell.nOrgs, 'organisatie', 'organisaties')}</li>
                        <li>{telwoord(cell.nRespondents, 'respondent', 'respondenten')}</li>
                    </ul>
                </div>

                {/* Eén paneel met tabbladen (besluit user 2026-09-15). De
                    mechanismen- en interviewtabs alleen op de mechanisme-
                    categoriedimensie, waar de server ze levert. Aantallen op
                    de tabs alleen als ze bekend zijn. */}
                <section className="intelligence-panel">
                    <div className="intelligence-subtabs" role="tablist">
                        {tabs.map((entry) => (
                            <button
                                key={entry.id}
                                type="button"
                                role="tab"
                                aria-selected={tab === entry.id}
                                className={`intelligence-subtab${tab === entry.id ? ' intelligence-subtab--active' : ''}`}
                                onClick={() => setTab(entry.id)}
                            >
                                {entry.label}
                                {typeof entry.aantal === 'number' && (
                                    <span className="intelligence-subtab__aantal">{entry.aantal}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {tab === 'onderbouwing' && (
                        <InsightCellsTable cells={[cell]} showNextTierGaps p2Targets={p2Targets} onClassificationClick={setUitlegCel} />
                    )}
                    {tab === 'orgs' && (
                        <CellOrgsTable orgEffects={orgEffects} contributors={cell.contributors || []} nRespondents={cell.nRespondents} />
                    )}
                    {tab === 'mechanisms' && mechanisms !== null && (
                        <>
                            <p className="intelligence-agenda__hint">
                                Wat de bijdragende organisaties in deze categorie in hun Theory of Change hebben staan, elk met de stand van zijn eigen cel.
                            </p>
                            <CellMechanismsSection mechanisms={mechanisms} />
                        </>
                    )}
                    {tab === 'interviews' && mechanisms !== null && (
                        <>
                            <p className="intelligence-agenda__hint">
                                Citaten die bij de analyse van een interview aan een van deze mechanismen zijn gehangen. Illustreert; bewijst niet.
                            </p>
                            {/* De poort per mechanisme, dezelfde telling als de brief van
                                Decca. Alleen als de server hem meestuurt: 'onbekend' mag
                                niet als 'niet gehaald' verschijnen.
                                AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading */}
                            {interviewGate && typeof interviewGate.orgs === 'number' && (
                                <p className={`intelligence-state${interviewGate.passed ? '' : ' intelligence-state--warn'}`}>
                                    {interviewGate.passed
                                        ? `Kwalitatief onderbouwd: ${interviewGate.orgs} organisaties met een citaat op dit mechanisme.`
                                        : `Nog niet kwalitatief onderbouwd: ${interviewGate.orgs} van minimaal ${interviewGate.minOrgs} organisaties hebben een citaat op dit mechanisme.`}
                                </p>
                            )}
                            <CellInterviewsSection interviews={interviews} />
                        </>
                    )}
                    {tab === 'insight' && (
                        <CellInsightBlock
                            cell={cell}
                            themeInsights={themeInsights}
                            themeTitle={theme ? themeTitle(theme) : ''}
                            benchmarkState={effectBenchmarkState(benchmark)}
                        />
                    )}
                </section>
            </>
        )
    }

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={kop}
                    description={'Eén cel van dit thema: de cijfers, per organisatie, de mechanismen erachter en wat interviews erover zeggen.'}
                    AI={false}
                    actions={<button type="button" onClick={terug}>Terug naar het thema</button>}
                />
                {inhoud()}
                <Modal
                    modalOpen={uitlegCel !== null}
                    setModalOpen={() => setUitlegCel(null)}
                    confirm={() => setUitlegCel(null)}
                    pageHeader='Classificatie inzicht'
                    hideConfirm
                    cancelLabel='Sluiten'
                    content={uitlegCel && (
                        <ClassificationExplanation
                            classification={uitlegCel.classification}
                            B={uitlegCel.B}
                            p2Level={uitlegCel.gates?.p2?.level ?? 0}
                            p2Passed={uitlegCel.gates?.p2?.passed}
                            p3Status={uitlegCel.gates?.p3}
                            contrast={uitlegCel.contrast}
                        />
                    )}
                />
            </div>
        </div>
    )
}

/**
 * De poort staat om de inhoud heen: geen hook voor wie er niet hoort te zijn.
 * Weergavekeuze — de échte poort zit server-side op de Cloud Functions.
 * AI-CONTEXT: See docs/pitfalls.md#security-cf-admin-tenant-bypass
 */
const CellDetail = () => {
    const client = Location()[1]
    if (!isSuperAdminTenant(client)) {
        return (
            <div className="main"><div className="main-container">
                <p className="intelligence-state">Deze pagina is alleen beschikbaar voor het Deccos-beheerteam.</p>
            </div></div>
        )
    }
    return <CellDetailContent />
}

export default CellDetail
